import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Chip,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  Info,
  Science,
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code,
} from '@mui/icons-material';
import { FiSave } from 'react-icons/fi';
import { whatsappTemplatesAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import { colors } from '../theme/modernTheme';
import WhatsAppTemplatePreview from '../components/WhatsAppTemplatePreview';

const TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
];

const TEMPLATE_LANGUAGES = [
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'es_ES', label: 'Spanish (Spain)' },
  { value: 'es_MX', label: 'Spanish (Mexico)' },
  { value: 'fr_FR', label: 'French (France)' },
  { value: 'de_DE', label: 'German' },
  { value: 'it_IT', label: 'Italian' },
  { value: 'pt_BR', label: 'Portuguese (Brazil)' },
  { value: 'pt_PT', label: 'Portuguese (Portugal)' },
  { value: 'hi_IN', label: 'Hindi' },
  { value: 'ar_AR', label: 'Arabic' },
  { value: 'zh_CN', label: 'Chinese (Simplified)' },
  { value: 'zh_HK', label: 'Chinese (Hong Kong)' },
  { value: 'zh_TW', label: 'Chinese (Taiwan)' },
  { value: 'ja_JP', label: 'Japanese' },
  { value: 'ko_KR', label: 'Korean' },
];

const HEADER_FORMATS = [
  { value: 'NONE', label: 'None' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
];

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick Reply' },
  { value: 'PHONE_NUMBER', label: 'Phone Number' },
  { value: 'URL', label: 'URL' },
];

// Helper function to extract named variables from text
const extractNamedVariables = (text) => {
  if (!text) return [];
  const regex = /\{\{([a-z0-9_]+)\}\}/g;
  const variables = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    variables.push(match[1]);
  }
  return variables;
};

// Helper function to validate named variables
const validateNamedVariables = (text, fieldName, isBodyText = false) => {
  if (!text) return null;

  // Check for invalid variable format (any {{...}} that doesn't match named format)
  const allVariables = text.match(/\{\{[^}]+\}\}/g) || [];
  const invalidVariables = allVariables.filter((v) => !v.match(/^\{\{[a-z0-9_]+\}\}$/));

  if (invalidVariables.length > 0) {
    return `${fieldName} contains invalid variable format. Variables must be lowercase letters, numbers, and underscores only (e.g., {{customer_name}}, {{order_id}}). Found: ${invalidVariables.join(', ')}`;
  }

  // Check if variables are at start or end (only for body text)
  if (isBodyText) {
    if (text.trim().startsWith('{{')) {
      return `${fieldName} cannot start with a variable`;
    }
    if (text.trim().endsWith('}}')) {
      return `${fieldName} cannot end with a variable`;
    }
  }

  // Check for duplicate variables
  const variables = extractNamedVariables(text);
  const duplicates = variables.filter((item, index) => variables.indexOf(item) !== index);
  if (duplicates.length > 0) {
    return `${fieldName} contains duplicate variables: ${[...new Set(duplicates)].join(', ')}`;
  }

  return null;
};

const getLabelForFields = (field) => {
  switch (field) {
    case 'headerSelection':
      return 'Header Format';
    case 'header':
      return 'Header text';
    case 'body':
      return 'Body text';
    case 'footer':
      return 'Footer text';
    case 'buttons':
      return 'Buttons';
    default:
      return field;
  }
};

// Rich Text Body Field Component
const RichTextBodyField = ({
  value,
  onChange,
  error,
  helperText,
  inputRef,
  onAddVariable,
  componentIndex,
}) => {
  const internalRef = useRef(null);

  // Keyboard shortcuts configuration
  const FORMATTING_SHORTCUTS = {
    bold: {
      key: 'b',
      shift: false,
      label: 'Bold (*text*)',
      displayShortcut: '⌘B / Ctrl+B',
    },
    italic: {
      key: 'i',
      shift: false,
      label: 'Italic (_text_)',
      displayShortcut: '⌘I / Ctrl+I',
    },
    strikethrough: {
      key: 's',
      shift: false,
      label: 'Strikethrough (~text~)',
      displayShortcut: '⌘S / Ctrl+S',
    },
    monospace: {
      key: 'e',
      shift: false,
      label: 'Monospace (```text```)',
      displayShortcut: '⌘E / Ctrl+E',
    },
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modifier = isMac ? e.metaKey : e.ctrlKey;

    if (!modifier) return;

    // Check each formatting shortcut
    for (const [formatType, config] of Object.entries(FORMATTING_SHORTCUTS)) {
      const shiftMatch = config.shift ? e.shiftKey : !e.shiftKey;
      if (e.key === config.key && shiftMatch) {
        e.preventDefault();
        e.stopPropagation(); // Prevent other handlers
        applyFormatting(formatType);
        return;
      }
    }
  };

  const applyFormatting = (formatType) => {
    const inputElement = internalRef.current;
    if (!inputElement) return;

    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;
    const selectedText = value.substring(start, end);
    const scrollTop = inputElement.scrollTop;

    let formattedText = '';
    let cursorOffset = 0;

    switch (formatType) {
      case 'bold':
        if (selectedText) {
          formattedText = `*${selectedText}*`;
          cursorOffset = selectedText.length + 2; // Move cursor after closing *
        } else {
          formattedText = '**';
          cursorOffset = 1; // Move cursor between **
        }
        break;
      case 'italic':
        if (selectedText) {
          formattedText = `_${selectedText}_`;
          cursorOffset = selectedText.length + 2;
        } else {
          formattedText = '__';
          cursorOffset = 1;
        }
        break;
      case 'strikethrough':
        if (selectedText) {
          formattedText = `~${selectedText}~`;
          cursorOffset = selectedText.length + 2;
        } else {
          formattedText = '~~';
          cursorOffset = 1;
        }
        break;
      case 'monospace':
        if (selectedText) {
          formattedText = `\`\`\`${selectedText}\`\`\``;
          cursorOffset = selectedText.length + 6;
        } else {
          formattedText = '``````';
          cursorOffset = 3;
        }
        break;
      default:
        return;
    }

    const newText = value.substring(0, start) + formattedText + value.substring(end);
    onChange({ target: { value: newText } });

    // Set cursor position after formatting
    setTimeout(() => {
      inputElement.focus();
      const newCursorPos = selectedText ? start + cursorOffset : start + cursorOffset;
      inputElement.setSelectionRange(newCursorPos, newCursorPos);
      inputElement.scrollTop = scrollTop;
    }, 0);
  };

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={6}
        placeholder="Body text"
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        error={Boolean(error)}
        helperText={helperText}
        required
        inputRef={(el) => {
          internalRef.current = el;
          if (inputRef) inputRef(el);
        }}
        FormHelperTextProps={{
          sx: {
            color: 'error.main',
            fontWeight: 500,
          },
        }}
        InputProps={{
          endAdornment: (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start', mt: 1 }}
            >
              {value.length}/1024
            </Typography>
          ),
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        {/* Formatting buttons */}
        <Box display="flex" gap={0.5}>
          {[
            { type: 'bold', Icon: FormatBold },
            { type: 'italic', Icon: FormatItalic },
            { type: 'strikethrough', Icon: FormatStrikethrough },
            { type: 'monospace', Icon: Code },
          ].map(({ type, Icon }) => {
            const config = FORMATTING_SHORTCUTS[type];
            return (
              <Tooltip
                key={type}
                title={
                  <Box>
                    <div>{config.label}</div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.8, marginTop: '2px' }}>
                      {config.displayShortcut}
                    </div>
                  </Box>
                }
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => applyFormatting(type)}
                  sx={{
                    color: 'text.secondary',
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <Icon fontSize="small" sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>

        {/* Add variable button */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={onAddVariable}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Add variable
          </Button>
          <Tooltip title="Use named variables like {{customer_name}}, {{order_id}}" placement="top">
            <IconButton size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
              <Info fontSize="small" sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default function WhatsAppTemplateFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);

  // Refs for text fields to handle cursor position
  const textFieldRefs = useRef({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    language: 'en_US',
    category: 'MARKETING',
  });

  const [components, setComponents] = useState([
    { type: 'HEADER', text: '', format: 'NONE', buttons: [], example: null },
    { type: 'BODY', text: 'Hello.', format: null, buttons: [], example: null },
    { type: 'FOOTER', text: '', format: null, buttons: [], example: null },
    { type: 'BUTTONS', text: '', format: null, buttons: [], example: null },
  ]);

  // State for variable examples (named variables)
  const [variableExamples, setVariableExamples] = useState({
    header: {}, // { variable_name: 'example_value' }
    body: {}, // { variable_name: 'example_value' }
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      loadTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await whatsappTemplatesAPI.getById(id);
      const templateData = response.data?.template;

      if (templateData) {
        setTemplate(templateData);
        setFormData({
          name: templateData.name || '',
          language: templateData.language || 'en_US',
          category: templateData.category || 'UTILITY',
        });

        if (templateData.components && templateData.components.length > 0) {
          // Ensure we always have all 4 component types
          const headerComp = templateData.components.find(
            (c) => c.type?.toLowerCase() === 'header',
          );
          const bodyComp = templateData.components.find((c) => c.type?.toLowerCase() === 'body');
          const footerComp = templateData.components.find(
            (c) => c.type?.toLowerCase() === 'footer',
          );
          const buttonsComp = templateData.components.find(
            (c) => c.type?.toLowerCase() === 'buttons',
          );

          // Extract variable examples from header
          const headerExamples = {};
          if (headerComp?.example?.header_text_named_params) {
            headerComp.example.header_text_named_params.forEach((param) => {
              headerExamples[param.param_name] = param.example;
            });
          }

          // Extract variable examples from body
          const bodyExamples = {};
          if (bodyComp?.example?.body_text_named_params) {
            bodyComp.example.body_text_named_params.forEach((param) => {
              bodyExamples[param.param_name] = param.example;
            });
          }

          setVariableExamples({
            header: headerExamples,
            body: bodyExamples,
          });

          setComponents([
            {
              type: 'HEADER',
              text: headerComp?.text || '',
              format: headerComp?.format?.toUpperCase() || 'NONE',
              buttons: [],
              example: headerComp?.example || null,
            },
            {
              type: 'BODY',
              text: bodyComp?.text || '',
              format: null,
              buttons: [],
              example: bodyComp?.example || null,
            },
            {
              type: 'FOOTER',
              text: footerComp?.text || '',
              format: null,
              buttons: [],
              example: footerComp?.example || null,
            },
            {
              type: 'BUTTONS',
              text: '',
              format: null,
              buttons:
                buttonsComp?.buttons?.map((btn) => ({
                  type: btn.type?.toUpperCase() || 'QUICK_REPLY',
                  text: btn.text || '',
                  phone_number: btn.phone_number || '',
                  url: btn.url || '',
                })) || [],
              example: buttonsComp?.example || null,
            },
          ]);
        }
      }
    } catch (err) {
      console.error('Error loading template:', err);
      showError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/whatsapp-templates');
  };

  const handleFormChange = (field, value) => {
    // Automatically replace spaces with underscores in template name
    let processedValue = field === 'name' ? value.replace(/\s+/g, '_') : value;

    // Enforce 512 character limit for template name
    if (field === 'name' && processedValue.length > 512) {
      return; // Don't update if exceeds limit
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleComponentChange = (index, field, value) => {
    // Validate character limits
    if (field === 'text') {
      const component = components[index];

      // Check for positional variables (not allowed)
      if (/\{\{\d+\}\}/.test(value)) {
        showError(
          'Positional variables like {{1}}, {{2}} are not allowed. Use named variables like {{customer_name}} instead.',
        );
        return;
      }

      if (component.type === 'HEADER' && value.length > 60) {
        return; // Don't update if exceeds limit
      }
      if (component.type === 'BODY' && value.length > 1024) {
        return; // Don't update if exceeds limit
      }
      if (component.type === 'FOOTER' && value.length > 60) {
        return; // Don't update if exceeds limit
      }

      // Validate named variables for HEADER and BODY
      if (component.type === 'HEADER' || component.type === 'BODY') {
        const isBodyText = component.type === 'BODY';
        const validationError = validateNamedVariables(
          value,
          component.type === 'HEADER' ? 'Header text' : 'Body text',
          isBodyText,
        );
        if (validationError) {
          setErrors((prev) => ({ ...prev, [`component_${index}_text`]: validationError }));
        } else {
          setErrors((prev) => ({ ...prev, [`component_${index}_text`]: null }));
        }

        // Extract variables and update examples state
        const variables = extractNamedVariables(value);
        const fieldKey = component.type === 'HEADER' ? 'header' : 'body';

        setVariableExamples((prev) => {
          const newExamples = { ...prev[fieldKey] };

          // Remove examples for variables that no longer exist
          Object.keys(newExamples).forEach((varName) => {
            if (!variables.includes(varName)) {
              delete newExamples[varName];
            }
          });

          // Add empty examples for new variables
          variables.forEach((varName) => {
            if (!newExamples[varName]) {
              newExamples[varName] = '';
            }
          });

          return { ...prev, [fieldKey]: newExamples };
        });
      }
    } else {
      // For non-text fields or fields without specific validation above, clear errors
      setErrors((prev) => ({ ...prev, [`component_${index}_${field}`]: null }));
    }

    setComponents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleVariableExampleChange = (componentType, variableName, value) => {
    const fieldKey = componentType === 'HEADER' ? 'header' : 'body';
    setVariableExamples((prev) => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        [variableName]: value,
      },
    }));

    // Clear any errors for this variable
    setErrors((prev) => ({ ...prev, [`${fieldKey}_${variableName}_example`]: null }));
  };

  const handleAddVariable = (componentIndex) => {
    const textFieldKey = `component_${componentIndex}`;
    const inputElement = textFieldRefs.current[textFieldKey];

    if (!inputElement) return;

    const cursorPos = inputElement.selectionStart || 0;
    const scrollTop = inputElement.scrollTop; // Save scroll position
    const currentText = components[componentIndex].text || '';
    const newText = currentText.slice(0, cursorPos) + '{{}}' + currentText.slice(cursorPos);

    handleComponentChange(componentIndex, 'text', newText);

    // Set cursor position in the middle of {{}} and restore scroll
    setTimeout(() => {
      inputElement.focus();
      inputElement.setSelectionRange(cursorPos + 2, cursorPos + 2);
      inputElement.scrollTop = scrollTop; // Restore scroll position
    }, 0);
  };

  const handleAddButton = (componentIndex) => {
    // Default to QUICK_REPLY for new buttons
    const validation = canAddButton(componentIndex, 'QUICK_REPLY');

    if (!validation.allowed) {
      showError(validation.reason);
      return;
    }

    setComponents((prev) => {
      const updated = [...prev];
      updated[componentIndex].buttons.push({
        type: 'QUICK_REPLY',
        text: '',
        phone_number: '',
        url: '',
      });
      return updated;
    });
  };

  const handleRemoveButton = (componentIndex, buttonIndex) => {
    setComponents((prev) => {
      const updated = [...prev];
      updated[componentIndex].buttons = updated[componentIndex].buttons.filter(
        (_, i) => i !== buttonIndex,
      );
      return updated;
    });
  };

  const handleButtonChange = (componentIndex, buttonIndex, field, value) => {
    // Validate button text length
    if (field === 'text' && value.length > 25) {
      return; // Don't update if exceeds limit
    }

    // Validate button type change
    if (field === 'type') {
      const buttons = components[componentIndex].buttons;
      const currentButton = buttons[buttonIndex];
      const currentType = currentButton.type;
      const newType = value;

      // If changing from quick reply to CTA or vice versa, validate limits
      if (currentType !== newType) {
        const otherButtons = buttons.filter((_, idx) => idx !== buttonIndex);
        const quickReplyCount = otherButtons.filter((btn) => btn.type === 'QUICK_REPLY').length;
        const ctaCount = otherButtons.filter(
          (btn) => btn.type === 'URL' || btn.type === 'PHONE_NUMBER',
        ).length;

        if (newType === 'QUICK_REPLY') {
          if (quickReplyCount >= 10) {
            showError('Maximum 10 Quick Reply buttons allowed');
            return;
          }
        } else {
          // URL or PHONE_NUMBER
          if (ctaCount >= 2) {
            showError('Maximum 2 Call-to-Action buttons (URL/Phone Number combined) allowed');
            return;
          }
        }
      }
    }

    setComponents((prev) => {
      const updated = [...prev];
      updated[componentIndex].buttons[buttonIndex] = {
        ...updated[componentIndex].buttons[buttonIndex],
        [field]: value,
      };
      return updated;
    });
  };

  // Calculate button counts for validation
  const getButtonCounts = (componentIndex) => {
    const buttons = components[componentIndex]?.buttons || [];
    const quickReplyCount = buttons.filter((btn) => btn.type === 'QUICK_REPLY').length;
    const ctaCount = buttons.filter(
      (btn) => btn.type === 'URL' || btn.type === 'PHONE_NUMBER',
    ).length;
    return { quickReplyCount, ctaCount, totalCount: buttons.length };
  };

  // Check if we can add a button of a specific type
  const canAddButton = (componentIndex, buttonType) => {
    const { quickReplyCount, ctaCount, totalCount } = getButtonCounts(componentIndex);

    // Max 10 total buttons
    if (totalCount >= 10) {
      return { allowed: false, reason: 'Maximum 10 buttons allowed per template' };
    }

    if (buttonType === 'QUICK_REPLY') {
      // Max 10 quick reply buttons (but recommend 3)
      if (quickReplyCount >= 10) {
        return { allowed: false, reason: 'Maximum 10 Quick Reply buttons allowed' };
      }
    } else {
      // Max 2 call-to-action buttons (URL or PHONE_NUMBER combined)
      if (ctaCount >= 2) {
        return {
          allowed: false,
          reason: 'Maximum 2 Call-to-Action buttons (URL/Phone Number combined) allowed',
        };
      }
    }

    return { allowed: true };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Template name is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Template name must be lowercase with underscores only';
    } else if (formData.name.length > 512) {
      newErrors.name = 'Template name cannot exceed 512 characters';
    }

    if (!formData.language) {
      newErrors.language = 'Language is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (components.length === 0) {
      newErrors.components = 'At least one component is required';
    }

    components.forEach((comp, index) => {
      if (comp.type === 'BODY' && (!comp.text || comp.text.trim() === '')) {
        newErrors[`component_${index}_text`] = 'Body text is required';
      }
      if (comp.type === 'HEADER' && comp.format === 'TEXT' && comp.text && comp.text.length > 60) {
        newErrors[`component_${index}_text`] = 'Header text cannot exceed 60 characters';
      }
      if (comp.type === 'BODY' && comp.text && comp.text.length > 1024) {
        newErrors[`component_${index}_text`] = 'Body text cannot exceed 1024 characters';
      }
      if (comp.type === 'FOOTER' && comp.text && comp.text.length > 60) {
        newErrors[`component_${index}_text`] = 'Footer text cannot exceed 60 characters';
      }

      // Validate named variables
      if (comp.type === 'HEADER' || comp.type === 'BODY') {
        const fieldName = comp.type === 'HEADER' ? 'Header text' : 'Body text';
        const isBodyText = comp.type === 'BODY';
        const validationError = validateNamedVariables(comp.text, fieldName, isBodyText);
        if (validationError) {
          newErrors[`component_${index}_text`] = validationError;
        }

        // Check that all variables have example values
        const variables = extractNamedVariables(comp.text);
        const fieldKey = comp.type === 'HEADER' ? 'header' : 'body';
        variables.forEach((varName) => {
          if (
            !variableExamples[fieldKey][varName] ||
            variableExamples[fieldKey][varName].trim() === ''
          ) {
            newErrors[`${fieldKey}_${varName}_example`] =
              `Example value required for variable {{${varName}}}`;
          }
        });
      }

      if (comp.type === 'BUTTONS' && comp.buttons.length === 0) {
        // Buttons are optional, so no error
      }
      comp.buttons?.forEach((btn, btnIndex) => {
        if (!btn.text || btn.text.trim() === '') {
          newErrors[`component_${index}_button_${btnIndex}_text`] = 'Button text is required';
        }
        if (btn.text && btn.text.length > 25) {
          newErrors[`component_${index}_button_${btnIndex}_text`] =
            'Button text cannot exceed 25 characters';
        }
        if (btn.type === 'PHONE_NUMBER' && (!btn.phone_number || btn.phone_number.trim() === '')) {
          newErrors[`component_${index}_button_${btnIndex}_phone`] = 'Phone number is required';
        }
        if (btn.type === 'URL' && (!btn.url || btn.url.trim() === '')) {
          newErrors[`component_${index}_button_${btnIndex}_url`] = 'URL is required';
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formData.name.trim(),
        language: formData.language,
        category: formData.category,
        components: components
          .filter((comp) => {
            // Include BODY always (required)
            if (comp.type === 'BODY') return true;
            // Include HEADER only if format is not NONE
            if (comp.type === 'HEADER') return comp.format && comp.format !== 'NONE';
            // Include FOOTER only if it has text
            if (comp.type === 'FOOTER') return comp.text && comp.text.trim() !== '';
            // Include BUTTONS only if it has buttons
            if (comp.type === 'BUTTONS') return comp.buttons && comp.buttons.length > 0;
            return false;
          })
          .map((comp) => {
            const component = {
              type: comp.type,
            };

            if (comp.format && comp.type === 'HEADER' && comp.format !== 'NONE') {
              component.format = comp.format;
            }

            if (comp.text && comp.text.trim() !== '') {
              component.text = comp.text;
            }

            // Add example values for named variables
            if (comp.type === 'HEADER' && comp.format === 'TEXT' && comp.text) {
              const headerVars = extractNamedVariables(comp.text);
              if (headerVars.length > 0) {
                component.example = {
                  header_text_named_params: headerVars.map((varName) => ({
                    param_name: varName,
                    example: variableExamples.header[varName] || '',
                  })),
                };
              }
            }

            if (comp.type === 'BODY' && comp.text) {
              const bodyVars = extractNamedVariables(comp.text);
              if (bodyVars.length > 0) {
                component.example = {
                  body_text_named_params: bodyVars.map((varName) => ({
                    param_name: varName,
                    example: variableExamples.body[varName] || '',
                  })),
                };
              }
            }

            if (comp.buttons && comp.buttons.length > 0) {
              component.buttons = comp.buttons.map((btn) => {
                const button = {
                  type: btn.type,
                  text: btn.text,
                };
                if (btn.phone_number) button.phone_number = btn.phone_number;
                if (btn.url) button.url = btn.url;
                return button;
              });
            }

            return component;
          }),
      };

      if (isEditMode) {
        await whatsappTemplatesAPI.update(id, payload);
        showSuccess('Template updated successfully');
      } else {
        await whatsappTemplatesAPI.create(payload);
        showSuccess('Template created successfully and is pending approval');
      }

      navigate('/whatsapp-templates');
    } catch (err) {
      console.error('Error saving template:', err);
      const errorMessage = err.error?.message || err.message || 'Failed to save template';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Generate preview template
  const previewTemplate = {
    name: formData.name || 'preview',
    language: formData.language,
    category: formData.category,
    status: 'PENDING',
    components: components
      .filter((comp) => {
        // Include BODY always (required)
        if (comp.type === 'BODY') return true;
        // Include HEADER only if format is not NONE
        if (comp.type === 'HEADER') return comp.format && comp.format !== 'NONE';
        // Include FOOTER only if it has text
        if (comp.type === 'FOOTER') return comp.text && comp.text.trim() !== '';
        // Include BUTTONS only if it has buttons
        if (comp.type === 'BUTTONS') return comp.buttons && comp.buttons.length > 0;
        return false;
      })
      .map((comp) => {
        const component = {
          type: comp.type, // Keep uppercase for preview component
          text: comp.text,
          format: comp.format,
          buttons: comp.buttons?.map((btn) => ({
            type: btn.type,
            text: btn.text,
            phone_number: btn.phone_number,
            url: btn.url,
          })),
        };

        // Add example for HEADER
        if (comp.type === 'HEADER' && comp.format === 'TEXT' && comp.text) {
          const headerVars = extractNamedVariables(comp.text);
          if (headerVars.length > 0) {
            component.example = {
              header_text_named_params: headerVars.map((varName) => ({
                param_name: varName,
                example: variableExamples.header[varName] || '',
              })),
            };
          }
        }

        // Add example for BODY
        if (comp.type === 'BODY' && comp.text) {
          const bodyVars = extractNamedVariables(comp.text);
          if (bodyVars.length > 0) {
            component.example = {
              body_text_named_params: bodyVars.map((varName) => ({
                param_name: varName,
                example: variableExamples.body[varName] || '',
              })),
            };
          }
        }

        return component;
      }),
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        p: { xs: 1, sm: 2, md: 2 },
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{ '&:hover': { color: 'rgba(0, 0, 0, 1)' } }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#000000', fontSize: '1.25rem' }}
            >
              {isEditMode ? 'Edit WhatsApp Template' : 'Create WhatsApp Template'}
            </Typography>
            {/* 
            // TODO: remove when feature is completed
            */}
            <Chip
              icon={<Science sx={{ fontSize: '0.875rem' }} />}
              label="Experimental"
              size="small"
              sx={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '24px',
                '& .MuiChip-icon': {
                  color: '#92400E',
                },
              }}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <FiSave />}
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            backgroundColor: colors.primary.main,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          {saving ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Template Details
            </Typography>

            {isEditMode && template?.status === 'APPROVED' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Editing approved templates may require re-approval from WhatsApp.
              </Alert>
            )}

            {/* Basic Info */}
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name || 'Use lowercase letters, numbers, and underscores only'}
              disabled={isEditMode}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {formData.name.length}/512
                  </Typography>
                ),
              }}
            />

            <Box display="flex" gap={2} sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={formData.language}
                  label="Language"
                  onChange={(e) => handleFormChange('language', e.target.value)}
                  disabled={isEditMode}
                >
                  {TEMPLATE_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleFormChange('category', e.target.value)}
                  disabled={isEditMode}
                >
                  {TEMPLATE_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Components */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Template Components
              </Typography>
            </Box>

            {/* Component List */}
            {components.map((component, index) => (
              <Card
                key={index}
                sx={{
                  p: 2,
                  mb: 2,
                  border: `1px solid ${colors.border.main}`,
                  boxShadow: 'none',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip
                      label={component.type}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                    {(component.type === 'FOOTER' || component.type === 'BUTTONS') && (
                      <Typography variant="caption" color="text.secondary">
                        · Optional
                      </Typography>
                    )}
                  </Box>
                </Box>

                {component.type === 'HEADER' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Header Format</InputLabel>
                    <Select
                      value={component.format || 'NONE'}
                      label={getLabelForFields('headerSelection')}
                      onChange={(e) => handleComponentChange(index, 'format', e.target.value)}
                    >
                      {HEADER_FORMATS.map((fmt) => (
                        <MenuItem key={fmt.value} value={fmt.value}>
                          {fmt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(component.type === 'BODY' || component.type === 'FOOTER') && (
                  <>
                    {component.type === 'BODY' ? (
                      <RichTextBodyField
                        value={component.text}
                        onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                        error={errors[`component_${index}_text`]}
                        helperText={errors[`component_${index}_text`]}
                        inputRef={(el) => (textFieldRefs.current[`component_${index}`] = el)}
                        onAddVariable={() => handleAddVariable(index)}
                        componentIndex={index}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        placeholder={getLabelForFields(component.type.toLowerCase())}
                        value={component.text}
                        onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                        error={Boolean(errors[`component_${index}_text`])}
                        helperText={errors[`component_${index}_text`]}
                        inputRef={(el) => (textFieldRefs.current[`component_${index}`] = el)}
                        FormHelperTextProps={{
                          sx: {
                            color: 'error.main',
                            fontWeight: 500,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {component.text.length}/60
                            </Typography>
                          ),
                        }}
                      />
                    )}

                    {/* Variable Examples for Body */}
                    {component.type === 'BODY' &&
                      (() => {
                        const bodyVars = extractNamedVariables(component.text);
                        if (bodyVars.length > 0) {
                          return (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: '#f9fafb',
                                borderRadius: 1,
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  color: '#111827',
                                  fontSize: '0.8125rem',
                                }}
                              >
                                Variable Samples
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 2 }}
                              >
                                Include samples of all variables to help Meta review your template.
                              </Typography>
                              {bodyVars.map((varName) => (
                                <Box
                                  key={varName}
                                  display="flex"
                                  gap={2}
                                  alignItems="start"
                                  sx={{ mb: 2, '&:last-child': { mb: 0 } }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '140px',
                                      p: 1.5,
                                      backgroundColor: '#fff',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      height: '40px',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        color: '#6b7280',
                                        fontSize: '0.8125rem',
                                      }}
                                    >
                                      {`{{${varName}}}`}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      value={variableExamples.body[varName] || ''}
                                      onChange={(e) =>
                                        handleVariableExampleChange('BODY', varName, e.target.value)
                                      }
                                      error={Boolean(errors[`body_${varName}_example`])}
                                      helperText={errors[`body_${varName}_example`]}
                                      required
                                      placeholder={`Enter content for {{${varName}}}`}
                                      InputLabelProps={{ shrink: false }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: '#fff',
                                        },
                                        '& .MuiInputLabel-root': {
                                          display: 'none',
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          );
                        }
                        return null;
                      })()}
                  </>
                )}

                {component.type === 'HEADER' && component.format !== 'NONE' && (
                  <>
                    <TextField
                      fullWidth
                      placeholder={getLabelForFields('header')}
                      value={component.text}
                      onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                      error={Boolean(errors[`component_${index}_text`])}
                      helperText={errors[`component_${index}_text`]}
                      inputRef={(el) => (textFieldRefs.current[`component_${index}`] = el)}
                      FormHelperTextProps={{
                        sx: {
                          color: 'error.main',
                          fontWeight: 500,
                        },
                      }}
                      InputProps={{
                        endAdornment:
                          component.format === 'TEXT' ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {component.text.length}/60
                            </Typography>
                          ) : null,
                      }}
                    />
                    {component.format === 'TEXT' && (
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={0.5}
                        mt={1}
                      >
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => handleAddVariable(index)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          Add variable
                        </Button>
                        <Tooltip title="Use named variables like {{first_name}}" placement="top">
                          <IconButton size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                            <Info fontSize="small" sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* Variable Examples for Header */}
                    {component.format === 'TEXT' &&
                      (() => {
                        const headerVars = extractNamedVariables(component.text);
                        if (headerVars.length > 0) {
                          return (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: '#f9fafb',
                                borderRadius: 1,
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  color: '#111827',
                                  fontSize: '0.8125rem',
                                }}
                              >
                                Variable Samples
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 2 }}
                              >
                                Include samples of all variables to help Meta review your template.
                              </Typography>
                              {headerVars.map((varName) => (
                                <Box
                                  key={varName}
                                  display="flex"
                                  gap={2}
                                  alignItems="start"
                                  sx={{ mb: 2, '&:last-child': { mb: 0 } }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '140px',
                                      p: 1.5,
                                      backgroundColor: '#fff',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      height: '40px',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        color: '#6b7280',
                                        fontSize: '0.8125rem',
                                      }}
                                    >
                                      {`{{${varName}}}`}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      value={variableExamples.header[varName] || ''}
                                      onChange={(e) =>
                                        handleVariableExampleChange(
                                          'HEADER',
                                          varName,
                                          e.target.value,
                                        )
                                      }
                                      error={Boolean(errors[`header_${varName}_example`])}
                                      helperText={errors[`header_${varName}_example`]}
                                      required
                                      placeholder={`Enter content for {{${varName}}}`}
                                      InputLabelProps={{ shrink: false }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: '#fff',
                                        },
                                        '& .MuiInputLabel-root': {
                                          display: 'none',
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          );
                        }
                        return null;
                      })()}
                  </>
                )}

                {component.type === 'BUTTONS' && (
                  <Box>
                    {component.buttons.map((button, btnIndex) => (
                      <Card
                        key={btnIndex}
                        sx={{
                          p: 2,
                          mb: 2,
                          backgroundColor: colors.background.default,
                          boxShadow: 'none',
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Button {btnIndex + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveButton(index, btnIndex)}
                            sx={{ color: colors.error.main }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Button Type</InputLabel>
                          <Select
                            value={button.type}
                            label="Button Type"
                            onChange={(e) =>
                              handleButtonChange(index, btnIndex, 'type', e.target.value)
                            }
                          >
                            {BUTTON_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          label="Button Text"
                          value={button.text}
                          onChange={(e) =>
                            handleButtonChange(index, btnIndex, 'text', e.target.value)
                          }
                          error={Boolean(errors[`component_${index}_button_${btnIndex}_text`])}
                          helperText={errors[`component_${index}_button_${btnIndex}_text`]}
                          sx={{ mb: 2 }}
                          required
                          InputProps={{
                            endAdornment: (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {button.text.length}/25
                              </Typography>
                            ),
                          }}
                        />

                        {button.type === 'PHONE_NUMBER' && (
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={button.phone_number}
                            onChange={(e) =>
                              handleButtonChange(index, btnIndex, 'phone_number', e.target.value)
                            }
                            error={Boolean(errors[`component_${index}_button_${btnIndex}_phone`])}
                            helperText={errors[`component_${index}_button_${btnIndex}_phone`]}
                            placeholder="+1234567890"
                          />
                        )}

                        {button.type === 'URL' && (
                          <TextField
                            fullWidth
                            label="URL"
                            value={button.url}
                            onChange={(e) =>
                              handleButtonChange(index, btnIndex, 'url', e.target.value)
                            }
                            error={Boolean(errors[`component_${index}_button_${btnIndex}_url`])}
                            helperText={errors[`component_${index}_button_${btnIndex}_url`]}
                            placeholder="https://example.com"
                          />
                        )}
                      </Card>
                    ))}

                    {component.buttons.length < 10 && (
                      <>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => handleAddButton(index)}
                          variant="outlined"
                          fullWidth
                          sx={{ textTransform: 'none' }}
                        >
                          Add Button
                        </Button>
                        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                          <Info
                            fontSize="small"
                            sx={{ fontSize: '0.875rem', color: colors.text.secondary }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Max 10 total buttons (Quick Reply: up to 10, URL/Phone: up to 2
                            combined)
                          </Typography>
                        </Box>
                      </>
                    )}

                    {errors[`component_${index}_buttons`] && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {errors[`component_${index}_buttons`]}
                      </Alert>
                    )}
                  </Box>
                )}
              </Card>
            ))}
          </Card>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              position: 'sticky',
              top: 20,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Preview
            </Typography>
            <WhatsAppTemplatePreview template={previewTemplate} showExampleValues={true} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
